import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import CancelMessageRequest from '@app/models/cancel-message-request';
import EnumValue from '@app/models/enum-value';
import MessageSearchRequest from '@app/models/message-search-request';
import PagedResults from '@app/models/paged-results';
import RegenerateMessageRequest from '@app/models/regenerate-message-request';
import { BehaviorSubject, Observable } from 'rxjs';

import { Message, MessageAdminViewModel } from '../models';
import { UserService } from '@oa/shared-core';

@Injectable()
export class MessageService {
  private readonly _componentName = 'MessageService';

  /**
   * All Message Type Enum Values
   */
  public messageTypes = new BehaviorSubject<EnumValue[]>(null);

  constructor(private http: HttpClient, private userService:UserService) {

    if (this.userService.isAdmin){
      http.get('system/enum').subscribe(
        (result) => {
          // console.log(`${this._componentName} system/enum subscription result=`, result);
  
          this.messageTypes.next((result as EnumValue[]).filter((x) => x.type === 'MessageTypeEnum'));
        },
        (err) => {
          console.error(err);
          
        }
      );
    }  
  }

  public getMessages(keyType: string, keyValue: number) {
    return this.http.get<Message[]>(`message/GetMessages/${keyType}/${keyValue}`);
  }

  public getMessageByBookingId(bookingId: string) {
    return this.http.get<Message[]>(`message/GetMessages/${bookingId}`);
  }

  public sendSMS(message: Message) {
    console.log('service sent a text', message);
    return this.http.post('message/SendSMS', message);
  }

  public sendEmail(message: Message) {
    console.log('service sent an email', message);
    return this.http.post('message/SendEmail', message);
  }

  /**
   * Gets recent messages in the queue (no filtering with this endpoint)\
   */
  public getRecentMessages() {
    return this.http.get<MessageAdminViewModel[]>('message/recent');
  }

  /**
   * Searches messages (Admin only)
   * @param request
   * @returns
   */
  public search(request: MessageSearchRequest) {
    return this.http.post<PagedResults>('message/search', request);
  }

  public patch<K extends keyof Message>(id: number, path: K, value: Message[K]): Observable<{}> {
    return this.patchJson(id, [
      {
        path,
        value,
        op: 'replace',
      },
    ]);
  }

  public patchJson<K extends keyof Message>(id: number, patch: MessagePatch<K>[]): Observable<Message> {
    return this.http.patch<Message>(
      `Message/${id}`,
      patch.map((p) => ({ ...p, path: `/${p.path}` }))
    );
  }

  /**
   * Requests the OA Platform to regenerate un-sent messages in the message queue
   * @param request
   */
  public regenerateMessages(request: RegenerateMessageRequest) {
    return this.http.post('message/regen', request);
  }

  /**
   * Sends a test message to OA Admin (admin that is logged in)
   * @param message
   */
  public sendTest(message: Message) {
    return this.http.post('message/test', message);
  }

  /**
   * Sends a single, already queued message
   * @param messageId
   * @returns
   */
  public sendSingleMessage(messageId: number) {
    return this.http.get(`message/${messageId}/send`);
  }

  /**
   * Resets a message in the queue so that it can be sent again
   * @param messageId
   * @returns
   */
  public requeueMessage(messageId: number) {
    return this.http.get(`message/${messageId}/reset`);
  }

  /**
   * Cancels a message
   * @param message
   * @returns
   */
  public cancelMessage(message: Message) {
    return this.http.post('message/cancel', { messageId: message.id, cancelReasonId: null } as CancelMessageRequest);
  }
}

export type MessagePatch<K extends string & keyof Message> = { path: K; value: Message[K]; op: 'replace' };
